import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { CertificationIcon } from 'foundations-assets';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { Box } from 'foundations-components/transitional/components/Box';
import { List } from 'foundations-components/transitional/components/List';
import { NanoProgress } from 'foundations-components/transitional/components/NanoProgress';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { rgba } from 'foundations-components/transitional/utils/color';
import styled, { useTheme } from 'foundations-theming/utils';
import ACIcon from '../icon/ACIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ThumbnailOverlay = styled.div.withConfig({
  displayName: "ACThumbnailOverlay__ThumbnailOverlay",
  componentId: "qv5xjk-0"
})(["height:100%;width:100%;color:", ";background-color:rgba(0,0,0,", ");"], ({
  theme
}) => theme.color['text-core-on-fill-default'], ({
  opacity
}) => 1 - opacity);
const ThumbnailHeader = styled.div.withConfig({
  displayName: "ACThumbnailOverlay__ThumbnailHeader",
  componentId: "qv5xjk-1"
})(({
  theme
}) => ({
  position: 'absolute',
  top: theme.space['0'],
  left: theme.space['0'],
  width: '100%',
  padding: theme.space['300'],
  background: `linear-gradient(180deg, ${rgba(theme.color['fill-accent-neutral-default'], 0.6)}, transparent)`,
  pointerEvents: 'none'
}));
const ThumbnailFooter = styled.div.withConfig({
  displayName: "ACThumbnailOverlay__ThumbnailFooter",
  componentId: "qv5xjk-2"
})(({
  theme
}) => ({
  padding: `${theme.space['100']} ${theme.space['300']}`,
  backgroundColor: rgba(theme.color['fill-accent-neutral-default'], 0.6)
}));
const ThumnailProgress = ({
  progress
}) => {
  const trellisTheme = useTheme();
  return /*#__PURE__*/_jsx(NanoProgress, {
    color: progress === 1 ? trellisTheme.color['fill-accent-teal-default'] : trellisTheme.color['fill-accent-orange-default'],
    value: progress * 100,
    zIndex: 0
  });
};
const CardCompletedMessage = ({
  message = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "academyCommon.progress.completed"
  })
}) => {
  const trellisTheme = useTheme();
  return /*#__PURE__*/_jsxs(TFlex, {
    align: "center",
    gap: 4,
    children: [/*#__PURE__*/_jsx(ACIcon, {
      color: trellisTheme.color['fill-accent-teal-default'],
      name: "checkmark"
    }), message]
  });
};
export const ThumbnailCategories = ({
  categories
}) => {
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(FormattedList, {
      lastWordConnector: ', ',
      twoWordsConnector: ', ',
      wordsConnector: ', ',
      children: categories.map(category => /*#__PURE__*/_jsx(FormattedMessage, {
        message: `academyCommon.categories.${category}`
      }, category))
    })
  });
};
const Footer = ({
  children,
  completed = false,
  progress,
  contentType,
  watermark
}) => {
  const isVideo = ['ACADEMY_VIDEO', 'VIDEO'].includes(contentType || '');
  const showFooter = completed || children || isVideo;
  if (showFooter) {
    return /*#__PURE__*/_jsx(View, {
      inlineSize: "100%",
      insetBlockEnd: "0",
      insetInlineStart: "0",
      position: "absolute",
      children: /*#__PURE__*/_jsx(ThumbnailFooter, {
        children: isVideo ? /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(ThumnailProgress, {
            progress: completed ? 1 : 0
          }), /*#__PURE__*/_jsxs(Flex, {
            justifyContent: "space-between",
            children: [/*#__PURE__*/_jsxs(List, {
              inline: true,
              wrapChildren: true,
              children: [completed && /*#__PURE__*/_jsx(CardCompletedMessage, {
                message: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "academyCommon.progress.watched"
                })
              }), children]
            }), watermark]
          })]
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(ThumnailProgress, {
            progress: progress
          }), /*#__PURE__*/_jsxs(TFlex, {
            justify: "between",
            children: [completed && /*#__PURE__*/_jsx(CardCompletedMessage, {}), children]
          })]
        })
      })
    });
  }
  return null;
};
const ACThumbnailOverlay = ({
  completed,
  remainingTime,
  contentType,
  categories,
  duration,
  children,
  header,
  footer,
  badge,
  watermark,
  contentTypeName
}) => {
  const progress = completed ? 1 : duration && remainingTime ? (duration - remainingTime) / duration : 0;
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsxs(ThumbnailOverlay, {
      opacity: completed ? 0.6 : 1,
      children: [/*#__PURE__*/_jsx(ThumbnailHeader, {
        children: /*#__PURE__*/_jsx(Flex, {
          gap: "300",
          inlineSize: "100%",
          justifyContent: "space-between",
          children: header !== null && header !== void 0 ? header : /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsxs(Box, {
              grow: 1,
              overflow: "hidden",
              children: [/*#__PURE__*/_jsxs(Flex, {
                alignItems: "center",
                gap: "100",
                children: [/*#__PURE__*/_jsx(Text, {
                  color: "fill-surface-default",
                  truncate: true,
                  children: contentTypeName || /*#__PURE__*/_jsx(FormattedMessage, {
                    message: `academyCommon.contentTypes.${contentType}`
                  })
                }), contentType === 'CERTIFICATION' && /*#__PURE__*/_jsx(CertificationIcon, {
                  color: "fill-accent-orange-default",
                  size: "200"
                })]
              }), categories && /*#__PURE__*/_jsx(ThumbnailCategories, {
                categories: categories
              })]
            }), badge && /*#__PURE__*/_jsx(Badge, {
              blockSize: "fit-content",
              use: "koala",
              children: badge
            })]
          })
        })
      }), children, /*#__PURE__*/_jsx(Footer, {
        completed: completed,
        contentType: contentType,
        progress: progress,
        watermark: watermark,
        children: footer
      })]
    })
  );
};
export default ACThumbnailOverlay;